import { AnalysisConfig, EnhancedPattern, AnalysisResult, PatternDistribution } from '@/types/trading-analysis-types';
import { StatisticalUtils } from '../utils/StatisticalUtils';
import { PatternClassifier } from '../utils/PatternClassifier';

export class BasicAnalyzer {
  protected config: AnalysisConfig;

  constructor(config: AnalysisConfig) {
    this.config = config;
  }

  /**
   * Analyze patterns using exact matching (current logic)
   */
  analyzePatterns(sequence: number[]): AnalysisResult {
    const startTime = Date.now();
    const patterns = new Map<string, { frequency: number; nextResults: number[]; lastSeenIndex: number }>();
    
    // Extract all patterns of specified lengths
    for (let length = this.config.minPatternLength; length <= this.config.maxPatternLength; length++) {
      for (let i = 0; i <= sequence.length - length - 1; i++) {
        const pattern = sequence.slice(i, i + length).join('');
        const nextResult = sequence[i + length];
        
        if (nextResult !== undefined) {
          if (!patterns.has(pattern)) {
            patterns.set(pattern, { frequency: 0, nextResults: [], lastSeenIndex: i });
          }
          
          const patternData = patterns.get(pattern)!;
          patternData.frequency++;
          patternData.nextResults.push(nextResult);
          patternData.lastSeenIndex = Math.max(patternData.lastSeenIndex, i);
        }
      }
    }
    
    // Create enhanced patterns with comprehensive analysis (NO HARD FILTERS)
    const enhancedPatterns: EnhancedPattern[] = Array.from(patterns.entries())
      .filter(([_, data]) => data.frequency >= this.config.minFrequency)
      .map(([pattern, data]) => {
        const winCount = data.nextResults.filter(r => r === 1).length;
        const lossCount = data.nextResults.filter(r => r === 0).length;
        const successRate = winCount / (winCount + lossCount) * 100;
        
        const enhancedPattern: EnhancedPattern = {
          pattern,
          frequency: data.frequency,
          nextWinCount: winCount,
          nextLossCount: lossCount,
          successRate: Math.round(successRate * 100) / 100,
          confidenceScore: this.calculateComprehensiveConfidenceScore(data.frequency, successRate, winCount + lossCount),
          lastSeenIndex: data.lastSeenIndex,
        };
        
        // Add pattern classification
        enhancedPattern.classification = PatternClassifier.classifyPattern(enhancedPattern);
        
        return enhancedPattern;
      })
      .sort((a, b) => {
        // Sort by classification priority, then confidence
        const categoryPriority = {
          'HIGH_WIN': 5, 'HIGH_LOSS': 4, 'MODERATE_WIN': 3, 
          'MODERATE_LOSS': 2, 'BALANCED': 1
        };
        const aPriority = categoryPriority[a.classification?.category || 'BALANCED'];
        const bPriority = categoryPriority[b.classification?.category || 'BALANCED'];
        
        if (aPriority !== bPriority) return bPriority - aPriority;
        return b.confidenceScore - a.confidenceScore;
      });

    const processingTime = Date.now() - startTime;
    
    // Calculate comprehensive metrics
    const patternDistribution = PatternClassifier.calculateDistribution(enhancedPatterns);
    const highRiskPatterns = PatternClassifier.getHighRiskPatterns(enhancedPatterns);
    const opportunityPatterns = PatternClassifier.getOpportunityPatterns(enhancedPatterns);

    return {
      mode: 'basic',
      patterns: enhancedPatterns,
      totalPatterns: enhancedPatterns.length,
      processingTime,
      patternDistribution,
      highRiskPatterns,
      opportunityPatterns,
    };
  }

  /**
   * Comprehensive confidence score calculation without bias
   */
  private calculateComprehensiveConfidenceScore(frequency: number, successRate: number, totalSamples: number): number {
    // Base score from frequency and sample size
    let baseScore = Math.min(100, (frequency * 10) + (totalSamples * 2));
    
    // Adjust for extreme success rates (both high and low can be valuable)
    if (successRate > 90 || successRate < 10) {
      // Extreme patterns get bonus if they have good sample size
      baseScore = totalSamples >= 10 ? baseScore + 20 : baseScore - 30;
    } else if (successRate > 80 || successRate < 20) {
      // Strong patterns get moderate bonus/penalty based on sample size
      baseScore = totalSamples >= 5 ? baseScore + 10 : baseScore - 15;
    }
    
    // Reliability bonus for high-frequency patterns
    if (frequency >= 10) baseScore += 15;
    else if (frequency >= 5) baseScore += 10;
    else if (frequency >= 3) baseScore += 5;
    
    // Sample size reliability
    if (totalSamples >= 20) baseScore += 10;
    else if (totalSamples >= 10) baseScore += 5;
    else if (totalSamples < 5) baseScore -= 20;
    
    return Math.max(10, Math.min(100, Math.round(baseScore)));
  }

  /**
   * Comprehensive dataset quality analysis
   */
  private analyzeDatasetQuality(patterns: EnhancedPattern[]): {
    totalPatterns: number;
    winRate: number;
    totalSamples: number;
    qualityScore: number;
    biasWarning?: string;
  } {
    if (patterns.length === 0) {
      return {
        totalPatterns: 0,
        winRate: 50,
        totalSamples: 0,
        qualityScore: 0,
        biasWarning: 'No patterns available'
      };
    }

    const totalWins = patterns.reduce((sum, p) => sum + p.nextWinCount, 0);
    const totalLosses = patterns.reduce((sum, p) => sum + p.nextLossCount, 0);
    const totalSamples = totalWins + totalLosses;
    const overallWinRate = totalSamples > 0 ? (totalWins / totalSamples) * 100 : 50;

    // Calculate quality score based on pattern distribution and reliability
    let qualityScore = 0;
    const highQualityPatterns = patterns.filter(p => 
      p.frequency >= 5 && p.successRate >= 40 && p.successRate <= 70
    ).length;
    const mediumQualityPatterns = patterns.filter(p => 
      p.frequency >= 3 && p.successRate >= 35 && p.successRate <= 75
    ).length;

    if (patterns.length > 0) {
      const highQualityRatio = highQualityPatterns / patterns.length;
      const mediumQualityRatio = mediumQualityPatterns / patterns.length;
      qualityScore = Math.round((highQualityRatio * 100 + mediumQualityRatio * 50) * 100) / 100;
    }

    // Detect bias
    let biasWarning: string | undefined;
    if (overallWinRate > 70) {
      biasWarning = `Strong WIN bias detected (${overallWinRate.toFixed(1)}% win rate) - predictions may be over-optimistic`;
    } else if (overallWinRate < 30) {
      biasWarning = `Strong LOSS bias detected (${overallWinRate.toFixed(1)}% win rate) - predictions may be over-pessimistic`;
    }

    return {
      totalPatterns: patterns.length,
      winRate: Math.round(overallWinRate * 100) / 100,
      totalSamples,
      qualityScore,
      biasWarning
    };
  }

  /**
   * Predict using STRICT exact pattern matching only with enhanced debugging
   */
  predictNextTrade(inputSequence: string, allPatterns: EnhancedPattern[]): {
    winProbability: number;
    lossProbability: number;
    confidenceLevel: 'very_low' | 'low' | 'medium' | 'high' | 'very_high';
    matchingPatterns: number;
    exactMatches: number;
    prediction: 'CERTAIN' | 'UNCERTAIN' | 'REFUSE';
    reason: string;
    debugInfo?: {
      datasetStats: { totalPatterns: number; winRate: number; totalSamples: number };
      exactMatches: Array<{ pattern: string; frequency: number; successRate: number; samples: number }>;
      rejectedMatches: Array<{ pattern: string; reason: string; frequency: number; successRate: number }>;
      qualityScore: number;
      biasWarning?: string;
    };
  } {
    const sequence = inputSequence.split(',').map(x => parseInt(x.trim()));
    
    // Validate sequence
    if (sequence.some(x => isNaN(x) || (x !== 0 && x !== 1))) {
      throw new Error('Invalid sequence format');
    }

    if (allPatterns.length === 0) {
      throw new Error('No patterns available for prediction');
    }

    // COMPREHENSIVE DATASET ANALYSIS
    const datasetStats = this.analyzeDatasetQuality(allPatterns);
    console.log(`📈 DATASET ANALYSIS:`, datasetStats);
    
    // Find ONLY exact matches at END of input sequence
    const inputString = sequence.join('');
    console.log(`🔍 DEBUG - Input sequence: "${inputString}" (length: ${inputString.length})`);
    
    const potentialMatches: Array<{ pattern: EnhancedPattern; matchType: string; reason?: string }> = [];
    
    allPatterns.forEach(pattern => {
      const isExactEndMatch = inputString.endsWith(pattern.pattern);
      const isExactFullMatch = inputString === pattern.pattern;
      const isSuffixMatch = pattern.pattern.endsWith(inputString);
      
      if (isExactEndMatch) {
        potentialMatches.push({ 
          pattern, 
          matchType: isExactFullMatch ? 'EXACT_FULL' : 'EXACT_SUFFIX',
          reason: `Pattern "${pattern.pattern}" matches end of input "${inputString}"`
        });
      } else if (isSuffixMatch && inputString.length < pattern.pattern.length) {
        potentialMatches.push({ 
          pattern, 
          matchType: 'PREFIX_MATCH',
          reason: `Input "${inputString}" is prefix of pattern "${pattern.pattern}"`
        });
      }
    });

    console.log(`🎯 POTENTIAL MATCHES ANALYSIS:`);
    potentialMatches.forEach(match => {
      console.log(`  - ${match.matchType}: "${match.pattern.pattern}" (freq: ${match.pattern.frequency}, success: ${match.pattern.successRate}%) - ${match.reason}`);
    });

    // Filter for only exact end matches (strict matching)
    const exactMatches = potentialMatches
      .filter(match => match.matchType === 'EXACT_FULL' || match.matchType === 'EXACT_SUFFIX')
      .map(match => match.pattern);

    console.log(`📊 STRICT EXACT MATCHES: ${exactMatches.length}`);

    // Apply comprehensive quality filtering to exact matches (no success rate bias)
    const rejectedMatches: Array<{ pattern: string; reason: string; frequency: number; successRate: number }> = [];
    
    const highQualityExactMatches = exactMatches.filter(p => {
      const isHighQuality = p.frequency >= 8 && 
        p.confidenceScore >= 70 &&
        (p.nextWinCount + p.nextLossCount) >= 15; // Minimum total sample size
      
      if (!isHighQuality) {
        const reasons = [];
        if (p.frequency < 8) reasons.push('low frequency');
        if (p.confidenceScore < 70) reasons.push('low confidence');
        if ((p.nextWinCount + p.nextLossCount) < 15) reasons.push('insufficient samples');
        
        rejectedMatches.push({
          pattern: p.pattern,
          reason: `Rejected for HIGH quality: ${reasons.join(', ')}`,
          frequency: p.frequency,
          successRate: p.successRate
        });
      } else {
        const patternType = p.successRate > 75 ? 'HIGH_WIN' : 
                           p.successRate > 55 ? 'MODERATE_WIN' :
                           p.successRate >= 45 ? 'BALANCED' :
                           p.successRate >= 25 ? 'MODERATE_LOSS' : 'HIGH_LOSS';
        console.log(`🏆 HIGH QUALITY: "${p.pattern}" (${patternType}, freq: ${p.frequency}, success: ${p.successRate}%, samples: ${p.nextWinCount + p.nextLossCount})`);
      }
      return isHighQuality;
    });

    const mediumQualityExactMatches = exactMatches.filter(p => {
      const isMediumQuality = p.frequency >= 5 && 
        p.confidenceScore >= 50 &&
        (p.nextWinCount + p.nextLossCount) >= 10; // Minimum total sample size
      
      const isNotHighQuality = !highQualityExactMatches.includes(p);
      
      if (isNotHighQuality && !isMediumQuality) {
        const reasons = [];
        if (p.frequency < 5) reasons.push('low frequency');
        if (p.confidenceScore < 50) reasons.push('low confidence');
        if ((p.nextWinCount + p.nextLossCount) < 10) reasons.push('insufficient samples');
        
        rejectedMatches.push({
          pattern: p.pattern,
          reason: `Rejected for MEDIUM quality: ${reasons.join(', ')}`,
          frequency: p.frequency,
          successRate: p.successRate
        });
      } else if (isMediumQuality && isNotHighQuality) {
        const patternType = p.successRate > 75 ? 'HIGH_WIN' : 
                           p.successRate > 55 ? 'MODERATE_WIN' :
                           p.successRate >= 45 ? 'BALANCED' :
                           p.successRate >= 25 ? 'MODERATE_LOSS' : 'HIGH_LOSS';
        console.log(`🥈 MEDIUM QUALITY: "${p.pattern}" (${patternType}, freq: ${p.frequency}, success: ${p.successRate}%, samples: ${p.nextWinCount + p.nextLossCount})`);
      }
      return isMediumQuality && isNotHighQuality;
    });

    // Collect debug info
    const debugInfo = {
      datasetStats: {
        totalPatterns: datasetStats.totalPatterns,
        winRate: datasetStats.winRate,
        totalSamples: datasetStats.totalSamples
      },
      exactMatches: exactMatches.map(p => ({
        pattern: p.pattern,
        frequency: p.frequency,
        successRate: p.successRate,
        samples: p.nextWinCount + p.nextLossCount
      })),
      rejectedMatches,
      qualityScore: datasetStats.qualityScore,
      biasWarning: datasetStats.biasWarning
    };

    // STRICT MATCHING LOGIC: Only proceed with statistically valid exact matches
    if (highQualityExactMatches.length >= 2) {
      // HIGH CONFIDENCE: Multiple high-quality exact matches with statistical validation
      let totalWins = 0;
      let totalLosses = 0;
      let validPatterns = 0;

      highQualityExactMatches.forEach(pattern => {
        // Statistical validation
        const chiSquareTest = StatisticalUtils.calculateChiSquare(
          { wins: pattern.nextWinCount, losses: pattern.nextLossCount },
          { winRate: 50 } // Compare against neutral expectation
        );
        
        const sampleSizeCheck = StatisticalUtils.isAdequateSampleSize(
          pattern.nextWinCount, 
          pattern.nextWinCount + pattern.nextLossCount
        );
        
        console.log(`📈 Statistical test for "${pattern.pattern}": p-value=${chiSquareTest.pValue.toFixed(3)}, significant=${chiSquareTest.isSignificant}, ${sampleSizeCheck.reason}`);
        
        if (sampleSizeCheck.isAdequate) {
          totalWins += pattern.nextWinCount;
          totalLosses += pattern.nextLossCount;
          validPatterns++;
        }
      });

      if (validPatterns >= 2) {
        const winProbability = (totalWins / (totalWins + totalLosses)) * 100;
        const avgFrequency = highQualityExactMatches.reduce((sum, p) => sum + p.frequency, 0) / highQualityExactMatches.length;
        
        console.log(`🎯 HIGH CONFIDENCE PREDICTION: ${winProbability.toFixed(1)}% win rate from ${validPatterns} statistically valid patterns`);

        return {
          winProbability: Math.round(winProbability * 100) / 100,
          lossProbability: Math.round((100 - winProbability) * 100) / 100,
          confidenceLevel: avgFrequency >= 10 ? 'very_high' : 'high',
          matchingPatterns: validPatterns,
          exactMatches: validPatterns,
          prediction: 'CERTAIN',
          reason: `Found ${validPatterns} statistically significant high-quality exact pattern matches (avg freq: ${avgFrequency.toFixed(1)})`,
          debugInfo
        };
      }
    }

    if (mediumQualityExactMatches.length >= 1) {
      // MEDIUM CONFIDENCE: At least one medium-quality exact match with statistical validation
      let totalWins = 0;
      let totalLosses = 0;
      let validPatterns = 0;

      mediumQualityExactMatches.forEach(pattern => {
        const sampleSizeCheck = StatisticalUtils.isAdequateSampleSize(
          pattern.nextWinCount, 
          pattern.nextWinCount + pattern.nextLossCount,
          3 // Lower threshold for medium quality
        );
        
        console.log(`📊 Medium quality check for "${pattern.pattern}": ${sampleSizeCheck.reason}`);
        
        if (sampleSizeCheck.isAdequate) {
          totalWins += pattern.nextWinCount;
          totalLosses += pattern.nextLossCount;
          validPatterns++;
        }
      });

      if (validPatterns >= 1) {
        const winProbability = (totalWins / (totalWins + totalLosses)) * 100;
        
        console.log(`📈 MEDIUM CONFIDENCE PREDICTION: ${winProbability.toFixed(1)}% win rate from ${validPatterns} valid patterns`);

        return {
          winProbability: Math.round(winProbability * 100) / 100,
          lossProbability: Math.round((100 - winProbability) * 100) / 100,
          confidenceLevel: validPatterns > 1 ? 'medium' : 'low',
          matchingPatterns: validPatterns,
          exactMatches: validPatterns,
          prediction: 'UNCERTAIN',
          reason: `Found ${validPatterns} adequate-quality exact pattern matches with sufficient sample size`,
          debugInfo
        };
      }
    }

    // NO RELIABLE MATCHES: Refuse to predict with detailed logging
    if (exactMatches.length === 0) {
      console.log(`❌ REFUSE: No exact pattern matches found for sequence "${inputString}"`);
      return {
        winProbability: 50.0,
        lossProbability: 50.0,
        confidenceLevel: 'very_low',
        matchingPatterns: 0,
        exactMatches: 0,
        prediction: 'REFUSE',
        reason: 'No exact pattern matches found - sequence has not been seen before',
        debugInfo
      };
    }

    // Found exact matches but they're too low quality - calculate weighted average instead of 50-50
    console.log(`⚠️ LOW QUALITY MATCHES: Found ${exactMatches.length} exact matches with low quality - calculating weighted average`);
    
    // Calculate weighted average based on actual success rates and frequencies
    let totalWeightedWins = 0;
    let totalWeightedSamples = 0;
    let calculationDetails: string[] = [];
    
    exactMatches.forEach(p => {
      const weight = p.frequency; // Use frequency as weight
      const patternWins = (p.successRate / 100) * weight;
      totalWeightedWins += patternWins;
      totalWeightedSamples += weight;
      
      calculationDetails.push(`"${p.pattern}": ${p.successRate}% × ${weight} freq = ${patternWins.toFixed(2)} weighted wins`);
      console.log(`  📊 "${p.pattern}": freq=${p.frequency}, success=${p.successRate}%, samples=${p.nextWinCount + p.nextLossCount}, weighted contribution=${patternWins.toFixed(2)}`);
    });
    
    const weightedWinProbability = totalWeightedSamples > 0 ? (totalWeightedWins / totalWeightedSamples) * 100 : 50;
    const weightedLossProbability = 100 - weightedWinProbability;
    
    console.log(`🧮 WEIGHTED CALCULATION: ${totalWeightedWins.toFixed(2)} weighted wins / ${totalWeightedSamples} total weight = ${weightedWinProbability.toFixed(1)}% win probability`);
    calculationDetails.forEach(detail => console.log(`  ${detail}`));
    
    // Enhanced debug info with calculation details
    const enhancedDebugInfo = {
      ...debugInfo,
      weightedCalculation: {
        totalWeightedWins: Math.round(totalWeightedWins * 100) / 100,
        totalWeight: totalWeightedSamples,
        calculationDetails,
        finalWinProbability: Math.round(weightedWinProbability * 100) / 100
      }
    };
    
    return {
      winProbability: Math.round(weightedWinProbability * 100) / 100,
      lossProbability: Math.round(weightedLossProbability * 100) / 100,
      confidenceLevel: 'very_low',
      matchingPatterns: exactMatches.length,
      exactMatches: exactMatches.length,
      prediction: 'UNCERTAIN',
      reason: `Found ${exactMatches.length} exact matches with low quality - calculated weighted average based on actual success rates (${weightedWinProbability.toFixed(1)}% win probability)`,
      debugInfo: enhancedDebugInfo
    };
  }
}